import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ICashFlow, IPropertyDetail, ILoans, ILoanEntry } from "../../../../interface/cashFlow";
import { formatCurrency, formatNumberWithDecimal, getCurrentFinancialYear, nth } from "../../../../utils/commonUtil";
import { REIButton } from "../../../../components/ui";
import EditLoan from "./EditLoan";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../../FilterSelectionContext";

interface LoanProps {
  cashFlowData: ICashFlow | undefined;
  year: string;
  isForecastOnly: boolean;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
}

const Loan: React.FC<LoanProps> = ({ cashFlowData, year, isForecastOnly, isEditable, setIsEditable }) => {
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const currentYear = "20" + getCurrentFinancialYear();
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  const TransformLoanData = (data: IPropertyDetail[], year: string) => {
    let resultData: any[] = [];
    data.forEach((property) => {
      const actualData = isShowingTotals
        ? property.Details.CashflowLoans?.Actuals.flat().filter((data) => data.Year == year) || []
        : property.Details.CashflowLoansPortion?.Actuals.flat().filter((data) => data.Year == year) || [];
      const forecastData = isShowingTotals
        ? property.Details.CashflowLoans?.Forecast.flat().filter((data) => data.Year == year) || []
        : property.Details.CashflowLoansPortion?.Forecast.flat().filter((data) => data.Year == year) || [];
      const varianceData = isShowingTotals
        ? property.Details.CashflowLoans?.Variance.flat().filter((data) => data.Year == year) || []
        : property.Details.CashflowLoansPortion?.Variance.flat().filter((data) => data.Year == year) || [];
      const loanNameArray =
        forecastData.map((item) => item.Name).filter((name) => name) ||
        actualData.map((item) => item.Name).filter((name) => name) ||
        varianceData.map((item) => item.Name).filter((name) => name);

      forecastData.forEach((data, index) => {
        if (!data.Name) {
          if (loanNameArray[index]) {
            data.Name = loanNameArray[index];
          } else {
            data.Name = `${index + 1}${nth(index + 1)} loan`;
          }
        }
      });

      actualData.forEach((data, index) => {
        if (!data.Name) {
          if (loanNameArray[index]) {
            data.Name = loanNameArray[index];
          } else {
            data.Name = `${index + 1}${nth(index + 1)} loan`;
          }
        }
      });

      varianceData.forEach((data, index) => {
        if (!data.Name) {
          if (loanNameArray[index]) {
            data.Name = loanNameArray[index];
          } else {
            data.Name = `${index + 1}${nth(index + 1)} loan`;
          }
        }
      });

      const allData = [
        { type: "Forecast", data: forecastData },
        { type: "Actuals", data: actualData },
        { type: "Variance", data: varianceData },
      ];

      let finalData: { loanName: string; data: { Actuals: []; Forecast: []; Variance: [] }[] }[] = [];
      let totalData: any[] = [];
      allData.forEach(({ type, data }) => {
        data.forEach((item) => {
          const addressParts = [
            property.PropertyDetail.StreetAddress,
            property.PropertyDetail.Suburb,
            property.PropertyDetail.State,
            property.PropertyDetail.Postcode,
            property.PropertyDetail.Country,
          ]
            .filter((part) => part && part.trim() !== "")
            .join(", ");
          const portfolioTrackerId = property.PropertyDetail.PortfolioTrackerId;
          totalData.push({
            property: addressParts,
            type,
            loanType: item.MortgageType || "",
            loanStart: item.LoanStart || "",
            term: item.Term || "",
            name: item.Name || "",
            lender: item.Name + (item.RefCode ? ` (${item.RefCode})` : "") || "",
            nextReview: item.InterestRateReview || "",
            interest: Number(item.InterestPayment) || 0,
            interestRate: Number(item.InterestRate) || 0,
            principal: Number(item.PrincipalPayment) || 0,
            balance: Number(item.Balance) || 0,
            portfolioTrackerId: portfolioTrackerId,
            loanRefCode: item.RefCode || "",
          });
        });
      });

      const loanNames = new Set(totalData.map((item) => item.name));
      loanNames.forEach((loanName) => {
        const types = ["Actuals", "Forecast", "Variance"];
        types.forEach((type) => {
          const hasType = totalData.some((item) => item.name === loanName && item.type === type);
          if (!hasType) {
            totalData.push({
              property: "",
              type,
              loanType: "",
              loanStart: "",
              term: "",
              name: loanName,
              lender: "",
              nextReview: "",
              interestRate: 0,
              principal: 0,
              balance: 0,
              portfolioTrackerId: "",
              loanRefCode: "",
            });
          }
        });
      });

      const separatedData = totalData.reduce((acc, item) => {
        if (!acc[item.name]) {
          acc[item.name] = [];
        }
        acc[item.name].push(item);
        return acc;
      }, {});

      Object.keys(separatedData).forEach((loanName) => {
        const name = separatedData[loanName][0]?.name || "Unknown Loan";
        finalData.push({ loanName: name, data: separatedData[loanName] });
      });
      if (finalData.length > 1) {
        let totalsAveragesData = CalculateTotalData(property, year);
        finalData.push(totalsAveragesData);
      }
      resultData.push(finalData);
    });
    return resultData;
  };

  const CalculateTotalData = (data: IPropertyDetail, year: string) => {
    const loansTotalsData = isShowingTotals
      ? data.Details.CashflowLoans?.LoansTotals || {}
      : data.Details.CashflowLoansPortion?.LoansTotals || {};
    const totalsAveragesData = {
      loanName: "Totals/Averages for this property",
      data: [
        {
          type: "Actuals",
          loanType: loansTotalsData.Actuals?.find((data) => data.Year == year)?.MortgageType || "",
          loanStart: loansTotalsData.Actuals?.find((data) => data.Year == year)?.LoanStart || "",
          term: "",
          name: "Totals/Averages for this property",
          lender: "",
          nextReview: loansTotalsData.Actuals?.find((data) => data.Year == year)?.InterestRateReview || "",
          interest: Number(loansTotalsData.Actuals?.find((data) => data.Year == year)?.InterestRate) || 0,
          interestRate: Number(loansTotalsData.Actuals?.find((data) => data.Year == year)?.InterestPayment) || 0,
          principal: Number(loansTotalsData.Actuals?.find((data) => data.Year == year)?.PrincipalPayment) || 0,
          balance: Number(loansTotalsData.Actuals?.find((data) => data.Year == year)?.Balance) || 0,
          portfolioTrackerId: "",
          property: "",
        },
        {
          type: "Forecast",
          loanType: loansTotalsData.Forecast?.find((data) => data.Year == year)?.MortgageType || "",
          loanStart: loansTotalsData.Forecast?.find((data) => data.Year == year)?.LoanStart || "",
          term: "",
          name: "Totals/Averages for this property",
          lender: "",
          nextReview: loansTotalsData.Forecast?.find((data) => data.Year == year)?.InterestRateReview || "",
          interest: Number(loansTotalsData.Forecast?.find((data) => data.Year == year)?.InterestRate) || 0,
          interestRate: Number(loansTotalsData.Forecast?.find((data) => data.Year == year)?.InterestPayment) || 0,
          principal: Number(loansTotalsData.Forecast?.find((data) => data.Year == year)?.PrincipalPayment) || 0,
          balance: Number(loansTotalsData.Forecast?.find((data) => data.Year == year)?.Balance) || 0,
          portfolioTrackerId: "",
          property: "",
        },
        {
          type: "Variance",
          loanType: loansTotalsData.Variance?.find((data) => data.Year == year)?.MortgageType || "",
          loanStart: loansTotalsData.Variance?.find((data) => data.Year == year)?.LoanStart || "",
          term: "",
          name: "Totals/Averages for this property",
          lender: "",
          nextReview: loansTotalsData.Variance?.find((data) => data.Year == year)?.InterestRateReview || "",
          interest: Number(loansTotalsData.Variance?.find((data) => data.Year == year)?.InterestRate) || 0,
          interestRate: Number(loansTotalsData.Variance?.find((data) => data.Year == year)?.InterestPayment) || 0,
          principal: Number(loansTotalsData.Variance?.find((data) => data.Year == year)?.PrincipalPayment) || 0,
          balance: Number(loansTotalsData.Variance?.find((data) => data.Year == year)?.Balance) || 0,
          portfolioTrackerId: "",
          property: "",
        },
      ],
    };
    return totalsAveragesData;
  };

  const TransformedFooter = (data: ILoans, year: string) => {
    const footerProperties = data || {};
    const actualFooter = footerProperties?.Actuals.find((data) => data.Year == year) || ({} as ILoanEntry);
    const forecastFooter = footerProperties?.Forecast.find((data) => data.Year == year) || ({} as ILoanEntry);
    const varianceFooter = footerProperties?.Variance.find((data) => data.Year == year) || ({} as ILoanEntry);

    return {
      Actuals: actualFooter,
      Forecast: forecastFooter,
      Variance: varianceFooter,
    };
  };

  const transformedData = useMemo(() => {
    if (cashFlowData) {
      return TransformLoanData(cashFlowData.PropertyDetails, year);
    }
    return [];
  }, [cashFlowData, year, isShowingTotals]);

  const transformedFooter = useMemo(() => {
    if (cashFlowData) {
      const loans = isShowingTotals
        ? cashFlowData?.TotalForDetails?.find((data) => data.EntityId === "")?.CashflowDetailsTotals.Loans
        : cashFlowData?.TotalForDetails?.find((data) => data.EntityId === "")?.CashflowDetailsPortions.Loans;
      if (!loans) {
        return undefined;
      }
      return TransformedFooter(loans, year);
    }
    return undefined;
  }, [cashFlowData, year, isShowingTotals]);

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const percentageTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatNumberWithDecimal(-value, 2, 2)}%)` : `${formatNumberWithDecimal(value, 2, 2)}%`}
    </span>
  );

  const currencyTemplate = (value: string) => (
    <span className={Number(value) < 0 ? "text-red-600" : ""}>
      {Number(value) < 0 ? `(${formatCurrency(-Number(value))})` : formatCurrency(Number(value))}
    </span>
  );

  const onCancel = () => {
    isEditable = false;
  };

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.name + rowData.property] || rowData.type === "Forecast"
      : !collapsedRows[rowData.name + rowData.property] || rowData.type === "Forecast";

    if (field === "lender" && rowData.type != "Forecast") {
      return null;
    }

    if (
      Number(year) > Number(currentYear) &&
      rowData.type !== "Forecast" &&
      rowData[field] !== "Variance" &&
      rowData[field] !== "Actuals"
    ) {
      return null;
    }

    return shouldRender ? templateFn(rowData[field]) : null;
  };

  return (
    cashFlowData && (
      <div className="rent-container">
        {isEditable ? (
          <EditLoan year={year} cashFlowData={cashFlowData} onCancel={onCancel} setIsEditable={setIsEditable} />
        ) : (
          <div>
            <DataTable size="small" value={[]} showHeaders emptyMessage="" className="hide-empty-message">
              <Column headerClassName="w-[10%] text-left" field="icon" header="" />
              <Column headerClassName="w-[10%] text-left" field="type" header="" />
              <Column headerClassName="w-[10%] text-left" field="loanType" header="Type" />
              <Column headerClassName="w-[10%] text-left" field="loanStart" header="Loan start" />
              <Column headerClassName="w-[10%] text-left" field="term" header="Term" />
              <Column headerClassName="w-[10%] text-left" field="lender" header="Name/Lender" />
              <Column headerClassName="w-[10%] text-left" field="nextReview" header="Rate review" />
              <Column headerClassName="w-[10%] text-left" field="interestRate" header="Interest rate" />
              <Column headerClassName="w-[10%] text-left" field="interest" header="Interest" />
              <Column headerClassName="w-[10%] text-left" field="loanAmount" header="Principal" />
              <Column headerClassName="w-[10%] text-left" field="balance" header="Balance" />
            </DataTable>

            {cashFlowData &&
              transformedData &&
              transformedData.length > 0 &&
              cashFlowData.PropertyDetails.length > 0 &&
              cashFlowData.PropertyDetails.map((property, index) => {
                const addressParts = [
                  property.PropertyDetail.StreetAddress,
                  property.PropertyDetail.Suburb,
                  property.PropertyDetail.State,
                  property.PropertyDetail.Postcode,
                  property.PropertyDetail.Country,
                ]
                  .filter((part) => part && part.trim() !== "")
                  .join(", ");

                return (
                  <div
                    className={` ${index % 2 === 1 ? "bg-gray-100" : ""}`}
                    key={property.PropertyDetail.PortfolioTrackerId}
                  >
                    <div
                      id={`mcfln-lnk-address-${property.PropertyDetail.PortfolioTrackerId}`}
                      data-testid={`mcfln-lnk-address-${property.PropertyDetail.PortfolioTrackerId}`}
                      className="text-left text-lg cursor-pointer text-blue-700"
                      onClick={() => {
                        navigate(`/portfolio-tracker`, {
                          state: {
                            tab: "MyPropertyTab",
                            MyPropertyId: property.PropertyDetail.PortfolioTrackerId,
                          },
                        });
                      }}
                    >
                      {addressParts}
                    </div>
                    {transformedData[index].map((loan, loanIndex) => (
                      <div key={loanIndex} className="mb-2">
                        <div className="text-left text-base font-semibold">
                          {loan.loanName} {loan.loanRefCode ? `(${loan.loanRefCode})` : ""}
                        </div>
                        <DataTable
                          value={loan.data}
                          rowGroupMode="rowspan"
                          groupRowsBy="name"
                          showHeaders={false}
                          size="small"
                          showGridlines={false}
                          rowClassName={() => (index % 2 === 1 ? "bg-gray-100" : "")}
                        >
                          {[
                            { field: "icon", header: "" },
                            { field: "type", header: "" },
                            { field: "loanType", header: "Type" },
                            { field: "loanStart", header: "Loan Start" },
                            {
                              field: "term",
                              header: "Term",
                              format: (value) =>
                                value === ""
                                  ? ""
                                  : value % 1 === 0
                                  ? `${value} yrs`
                                  : `${formatNumberWithDecimal(value, 1, 1)} yrs`,
                            },
                            {
                              field: "lender",
                              header: "Lender",
                            },
                            { field: "nextReview", header: "Next Review" },
                            { field: "interestRate", header: "Interest Rate", format: percentageTemplate },
                            { field: "interest", header: "Interest", format: currencyTemplate },
                            { field: "principal", header: "Loan Amount", format: currencyTemplate },
                            { field: "balance", header: "Balance", format: currencyTemplate },
                          ].map(({ field, header, format }) => (
                            <Column
                              key={field}
                              className="w-[10%] text-left"
                              field={field}
                              header={header}
                              body={(rowData) =>
                                field === "icon" ? (
                                  <div className="flex items-center justify-center">
                                    {isForecastOnly && rowData.type === "Forecast" && (
                                      <REIButton
                                        id={`mcfln-btn-collapse-${property.PropertyDetail.PortfolioTrackerId}-${loanIndex}`}
                                        icon={
                                          collapsedRows[rowData.name + rowData.property]
                                            ? "pi pi-angle-down"
                                            : "pi pi-angle-right"
                                        }
                                        text={true}
                                        onClick={() => toggleRowCollapse(rowData.name + rowData.property)}
                                      />
                                    )}
                                    {!isForecastOnly && rowData.type === "Forecast" && (
                                      <REIButton
                                        id={`mcfln-btn-collapse-${property.PropertyDetail.PortfolioTrackerId}-${loanIndex}`}
                                        icon={
                                          collapsedRows[rowData.name + rowData.property]
                                            ? "pi pi-angle-right"
                                            : "pi pi-angle-down"
                                        }
                                        text={true}
                                        onClick={() => toggleRowCollapse(rowData.name + rowData.property)}
                                      />
                                    )}
                                  </div>
                                ) : format ? (
                                  renderCell(rowData, field, format)
                                ) : (
                                  renderCell(rowData, field, (value) => value)
                                )
                              }
                            />
                          ))}
                        </DataTable>
                      </div>
                    ))}
                  </div>
                );
              })}

            <div>
              {transformedFooter && (
                <>
                  <div className="text-start" style={{ color: "#374151", fontWeight: "bold" }}>
                    Totals/averages
                  </div>
                  <DataTable
                    value={[
                      { ...transformedFooter.Forecast, type: "Forecast" },
                      ...(!isForecastOnly && Number(year) <= Number(currentYear)
                        ? [
                            { ...transformedFooter.Actuals, type: "Actuals" },
                            { ...transformedFooter.Variance, type: "Variance" },
                          ]
                        : []),
                    ]}
                    showHeaders={false}
                    emptyMessage=""
                    rowGroupMode="rowspan"
                    className="hide-empty-message text-end"
                    size="small"
                  >
                    <Column className="text-left w-[10%]" field="" />
                    <Column
                      className="text-left w-[10%]"
                      style={{ color: "#374151", fontWeight: "bold" }}
                      field="type"
                    />
                    <Column className="text-left w-[10%]" field="loanType" />
                    <Column className="text-left w-[18%]" field="loanStart" />
                    <Column className="text-left w-[19%]" field="term" />
                    <Column
                      className="text-left w-[9%]"
                      field="InterestRate"
                      body={(rowData) => percentageTemplate(rowData.InterestRate)}
                    />
                    <Column
                      className="text-left w-[10%]"
                      field="InterestPayment"
                      body={(rowData) => currencyTemplate(rowData.InterestPayment)}
                    />

                    <Column
                      className="text-left w-[10%]"
                      field="PrincipalPayment"
                      body={(rowData) => currencyTemplate(rowData.PrincipalPayment)}
                    />
                    <Column className="text-left w-full" field="balance" />
                  </DataTable>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default Loan;
